/**
 * MonadExplainer — progressive-disclosure explainer for monads.
 *
 * A monad `(T, η, μ)` is the categorical answer to "how do effectful steps
 * compose?". In CSK it is the structure that lets a commitment be made,
 * carried, and discharged without ever escaping the `T` envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  KleisliCompositionDiagram,
  MonadDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR.PRECOMMIT;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-monad",
  kind: "Categorical structure",
  title: "Monad",
  subtitle: "(T, η : Id ⇒ T, μ : T ∘ T ⇒ T)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Comonad", href: "#explainer-comonad" },
  { label: "Graded monad", href: "#explainer-graded-monad", accent: ACCENT },
  { label: "Bimonad", href: "#explainer-bimonad" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "PRECOMMIT (W)", href: "#explainer-graded-monad", accent: ACCENT },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>monad</strong> is a typed envelope <code>T</code> for values together with two
      rules: how to put a value <em>into</em> the envelope (<code>η</code>), and how to collapse a
      double-wrapped envelope into a single one (<code>μ</code>). CSK uses monads to make "something
      happens with effects" compose without ever leaking the effects out.
    </p>
  ),
  intuition: (
    <>
      <p>
        Imagine you write a small function that, given a username, looks up the account in a
        database. It does not return an account — it returns "a recipe that, when executed, will
        produce an account or an error". That recipe is the value wrapped in an envelope. Now
        compose two such functions: "look up account, then look up its most recent order." If you
        try to glue them by ordinary function composition the types do not line up — the second
        function wants an account, but the first hands you a recipe. The monad's job is to teach
        recipes how to compose anyway.
      </p>
      <p>Three everyday examples each give one different envelope:</p>
      <ul>
        <li>
          <code>Maybe</code> — "this step might fail." <code>η a = Just a</code>; <code>μ</code>{" "}
          collapses <code>Just (Just a)</code> to <code>Just a</code> and any <code>Nothing</code>{" "}
          stays <code>Nothing</code>. Failures short-circuit by construction.
        </li>
        <li>
          <code>List</code> — "this step is nondeterministic." <code>η a = [a]</code>;{" "}
          <code>μ</code> flattens a list of lists. Two nondeterministic steps explore the cross
          product.
        </li>
        <li>
          <code>IO</code> — "this step does something to the outside world." <code>η a</code> is the
          action that returns <code>a</code> without doing anything; <code>μ</code> sequences two
          scheduled actions into one.
        </li>
      </ul>
      <p>
        The miracle is that the same three laws — left unit, right unit, associativity — make all
        three behave like ordinary composition. That is why a single <code>do</code>-block can
        thread error handling, nondeterminism, and IO without anyone ever writing the plumbing by
        hand.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`C`}</MathInline> be a category. A <strong>monad</strong> on{" "}
        <MathInline>{tex`C`}</MathInline> is an endofunctor{" "}
        <MathInline>{tex`T : C \to C`}</MathInline> together with two natural transformations:
      </p>
      <ul>
        <li>
          <strong>Unit</strong> <MathInline>{tex`\eta : \mathrm{Id} \Rightarrow T`}</MathInline> —
          for every <MathInline>{tex`A`}</MathInline> there is a map{" "}
          <MathInline>{tex`\eta_{A} : A \to T(A)`}</MathInline>.
        </li>
        <li>
          <strong>Multiplication</strong>{" "}
          <MathInline>{tex`\mu : T \circ T \Rightarrow T`}</MathInline> — for every{" "}
          <MathInline>{tex`A`}</MathInline> there is a map{" "}
          <MathInline>{tex`\mu_{A} : T(T(A)) \to T(A)`}</MathInline>.
        </li>
      </ul>
      <MonadDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        Subject to three coherence laws that say <MathInline>{tex`\eta`}</MathInline> is a two-sided
        identity for <MathInline>{tex`\mu`}</MathInline> and <MathInline>{tex`\mu`}</MathInline> is
        associative:
      </p>
      <ul>
        <li>
          <strong>Left unit:</strong>{" "}
          <MathInline>{tex`\mu \circ T \eta = \mathrm{id}_{T}`}</MathInline>
        </li>
        <li>
          <strong>Right unit:</strong>{" "}
          <MathInline>{tex`\mu \circ \eta T = \mathrm{id}_{T}`}</MathInline>
        </li>
        <li>
          <strong>Associativity:</strong>{" "}
          <MathInline>{tex`\mu \circ T \mu = \mu \circ \mu T`}</MathInline>
        </li>
      </ul>
      <p style={{ marginTop: "0.75rem" }}>
        <strong>Kleisli composition</strong> is the practical face of these laws. A{" "}
        <em>Kleisli arrow</em> from <MathInline>{tex`A`}</MathInline> to{" "}
        <MathInline>{tex`B`}</MathInline> is an ordinary morphism{" "}
        <MathInline>{tex`f : A \to T(B)`}</MathInline>. Two Kleisli arrows{" "}
        <MathInline>{tex`f : A \to T(B)`}</MathInline> and{" "}
        <MathInline>{tex`g : B \to T(C)`}</MathInline> compose into{" "}
        <MathInline>{tex`g \circ_{T} f : A \to T(C)`}</MathInline> via
      </p>
      <MathBlock ariaLabel="g ∘_T f equals μ ∘ T(g) ∘ f">
        {tex`g \circ_{T} f \;=\; \mu \circ T(g) \circ f`}
      </MathBlock>
      <KleisliCompositionDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        Kleisli composition is what makes <code>do</code>-notation, <code>{">>="}</code>, and
        for-comprehensions work. The same three laws guarantee that it is associative with{" "}
        <MathInline>{tex`\eta`}</MathInline> as identity — i.e., the{" "}
        <strong>Kleisli category</strong> of <MathInline>{tex`T`}</MathInline> is an honest
        category.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Monads matter to CSK because they are <em>the</em> categorical home for sequencing under an
        effect. Three concrete payoffs:
      </p>
      <ul>
        <li>
          <strong>Commitments cannot escape.</strong> If "issuing a commitment" is exactly
          "producing a <code>T(_)</code>", then the type system refuses any program that pretends to
          consume a commitment without going through <code>μ</code> — i.e., without running the
          discharge step. <code>PRECOMMIT</code> turns this into a structural safety claim.
        </li>
        <li>
          <strong>Effects compose by construction.</strong> Authorization checks, budget deductions,
          audit-log entries, and async dispatch each carry their own discipline. Monads keep that
          discipline attached to the value through every <code>{">>="}</code> rather than asking the
          programmer to remember it.
        </li>
        <li>
          <strong>
            The seam is the absence of <code>μ</code>.
          </strong>{" "}
          A bridge that ought to cross a seam is exactly a Kleisli arrow whose <code>T</code> wears
          a governance grade. When the corresponding grade is not derivable from the grading monoid,
          the composition is structurally inexpressible. (See the graded-monad card for the full
          story.)
        </li>
      </ul>
      <p>
        Read in this light, a monad is not "an abstract container" — it is the smallest piece of
        structure that lets a program <em>say</em> "I owe a discharge step" and have the type system
        enforce that the discharge actually happens before the value is consumed.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Canonical reference: monad definition, Kleisli category, Eilenberg–Moore algebras.",
    },
    {
      label: "Monad (MathLib, Agda)",
      path: "agda/MathLib/Monad/Monad.agda",
      note: "Re-export of upstream agda-categories monad surface used across CSK.",
    },
    {
      label: "MonadFromAdjunction (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/MonadFromAdjunction.agda",
      note: "Every adjunction induces a monad: T = G ∘ F with μ = G ε F.",
    },
    {
      label: "Theorem Canon",
      path: "docs/reference/Theorem-Canon.md",
      note: "Where PRECOMMIT (W) is recorded as the graded-monad instance.",
    },
  ],
};

export function MonadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<MonadDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
